import React from 'react';
import { COLOR, formatDOB, formatActivity, formatLastUpdated } from 'pages/Dashboard/components/PatientCardUtils';
import { Box, Grid, Typography, Fade, Menu, MenuItem, Alert, Tooltip } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import { AddPatientPayload, PatientObj, PatientStatusResponse } from 'pages/Dashboard/types/patient.types';
import LoadingButton from 'components/Common/LoadingButton';
import RecordSession from 'pages/Dashboard/components/SessionRecording/RecordSession';
import { trackMixpanelEvent } from 'utils/utilMethods';
import { MixpanelEventName } from 'utils/constants';
import { downloadUrl } from 'utils/service';
import { capitalize } from 'utils/appUtils';
import { fetchPatientExportData } from 'pages/Dashboard/services/whoiam.services';
import { useMutation, UseQueryResult } from 'react-query';
import { useHttp } from 'hooks/use-fetch';
import { Status } from 'pages/Dashboard/types';
import { InfoOutlined } from '@mui/icons-material';
import theme from 'theme';
import RecordRefreshDrawer from 'pages/Dashboard/RecordRefreshDrawer';
import { useParams } from 'react-router-dom';
import { getThirtyDayStatusChipAndTooltip } from 'pages/Dashboard/components/PatientCard';
import HLCard from 'components/HLCard';
import { GoogleDate } from 'utils/dateUtils';
import { HrRefreshHistory } from 'pages/Dashboard/types/healthRecords.types';

interface PatientDetailsPanelProps {
  patientDetails?: AddPatientPayload;
  recordingStatus: Status;
  setSelectedChip: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedTab: (tabIndex: number) => void;
  fetchSessions: ({ offset }: { offset: number }) => void;
  scheduledOn: GoogleDate | null;
  hrRefreshHistory: UseQueryResult<HrRefreshHistory, unknown>;
  hrRecordStatus: UseQueryResult<PatientStatusResponse, unknown>;
  isFetchingHR: boolean;
}

function PatientDetailsPanel({
  patientDetails,
  recordingStatus,
  setSelectedChip,
  setSelectedTab,
  fetchSessions,
  scheduledOn,
  hrRefreshHistory,
  hrRecordStatus,
  isFetchingHR,
}: PatientDetailsPanelProps) {
  const { id } = useParams();

  const {
    http,
    providerId,
    superAdminId,
    isSessionRecordingEnabled,
    setToastProps,
  } = useHttp();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [showRecordRefreshDrawer, setShowRecordRefreshDrawer] = React.useState(false);

  // Extract and format patient info from PatientObj

  const patient: PatientObj | undefined = patientDetails?.patient;
  const name = patient?.name?.firstName && patient?.name?.lastName
    ? `${patient.name.firstName} ${patient.name.lastName}`
    : patient?.alias || 'Unknown';
  const dobString = formatDOB(patient?.dateOfBirth);
  const activity = formatActivity(patient?.patientActivity) || '';
  const lastUpdated = scheduledOn
    ? formatLastUpdated(scheduledOn)
    : null;

  const open = Boolean(anchorEl);

  const isPendingApproval = [
    'PATIENT_APPROVAL_STATUS_PENDING',
    'PATIENT_APPROVAL_STATUS_REJECTED',
  ].includes(patientDetails?.patient?.approvalStatus ?? '');

  const patientName = React.useMemo(() => {
    const firstName = capitalize(patientDetails?.patient?.name?.firstName ?? '');
    const lastName = capitalize(patientDetails?.patient?.name?.lastName ?? '');
    return `${firstName} ${lastName}`;
  }, [patientDetails?.patient?.name?.firstName, patientDetails?.patient?.name?.lastName]);

  const { chip, tooltip } = getThirtyDayStatusChipAndTooltip(
    patientDetails?.patient?.thirtyDayStatus,
  );

  const { mutateAsync: fetchExportData, isLoading: exportLoading } = useMutation(
    (asPDF?: boolean) => fetchPatientExportData(http.post, providerId, id ?? '', asPDF),
    {
      onSuccess: (dataBlob) => {
        const fileName = `${patientName} Report`;
        const blob = new Blob([dataBlob], {
          type: dataBlob.type === 'application/pdf' ? dataBlob.type : 'application/vnd.ms-excel',
        });
        const url = window.URL.createObjectURL(blob);
        downloadUrl(url, fileName);
      },
    },
  );

  const handleClick = (event?: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event?.currentTarget ?? null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const exportData = (type: string) => {
    handleMenuClose();
    fetchExportData(type === 'pdf');
    trackMixpanelEvent(MixpanelEventName.PATIENT_DETAILS_EXPORT_DATA_BUTTON_CLICKED, {
      'patient id': id ?? '',
      'export type': type,
    });
  };

  const fetchSessionHandler = ({ offset }: { offset: number }) => {
    setSelectedTab(2);
    fetchSessions({ offset });
  };

  // const isDemoPatient = React.useMemo(
  //   () => patientDetails?.patient?.demoPatient ?? false,
  //   [patientDetails?.patient?.demoPatient],
  // );

  return (
    <Box>
      {[Status.RECORDING, Status.PAUSED].includes(recordingStatus) && (
      <Alert
        severity={recordingStatus === Status.RECORDING ? 'error' : 'warning'}
        icon={<InfoOutlined />}
        sx={{
          border: `1px solid ${theme.custom.colors.lightErrorMain}`,
          my: 1.5,
          '& .MuiAlert-icon': {
            color: theme.custom.colors.lightErrorMain,
            display: 'flex',
            alignItems: 'center',
          },
        }}
      >
        <Typography variant='body1' color={theme.custom.colors.lightTextPrimary}>
          {recordingStatus === Status.RECORDING
            ? 'Your session is being'
            : 'Session recording is'}
          {' '}
          <Typography
            variant='body1'
            fontWeight={700}
            component='span'
            color={recordingStatus !== Status.RECORDING ? '#541313' : '#5F2B01'}
          >
            {recordingStatus === Status.RECORDING ? 'recorded' : 'paused'}
          </Typography>
        </Typography>
      </Alert>
      )}
      <HLCard
        sx={{
          p: 2,
          mb: 2,
          minHeight: 100,
        }}
      >
        <Grid container spacing={1.5} alignItems='flex-start' justifyContent='space-between'>
          {/* Left: Patient Details */}
          <Grid item xs={12} md='auto'>
            <Box display='flex' gap={2} alignItems='flex-start'>
              <Box>
                <Typography variant='h6' fontWeight={600} fontSize={20} color={theme.custom.colors.lightTextPrimary}>
                  {name}
                </Typography>
                <Box display='flex' alignItems='center' gap={1} mb={0.5}>
                  <Typography variant='caption' color='text.secondary'>
                    {dobString}
                  </Typography>
                  {activity && (
                  <Typography variant='caption' color={COLOR.grayText} fontWeight={500}>
                    &#8226;
                  </Typography>
                  )}
                  {activity && (
                  <Typography variant='caption' color='text.secondary'>
                    {activity}
                  </Typography>
                  )}
                </Box>
                {lastUpdated && (
                <Typography variant='caption' color='text.secondary'>
                  Health records last updated
                  {' '}
                  <Typography component='span' variant='caption' sx={{ fontWeight: 600, color: theme.custom.colors.lightTextSecondary }}>
                    {lastUpdated}
                  </Typography>
                </Typography>
                )}
              </Box>
            </Box>
          </Grid>
          {/* Right: Actions */}
          <Grid item container xs={12} md='auto' spacing={1} justifyContent='flex-end' alignItems='flex-start' flex={1}>
            <Grid item xs={12} md='auto'>
              <RecordRefreshDrawer
                patientId={id}
                isOpen={showRecordRefreshDrawer}
                setShowRecordRefreshDrawer={setShowRecordRefreshDrawer}
                setSelectedChip={setSelectedChip}
                // isDemoPatient={isDemoPatient}
                hrRefreshHistory={hrRefreshHistory}
                hrRecordStatus={hrRecordStatus}
                isPendingApproval={isPendingApproval}
                isFetchingHR={isFetchingHR}
                drawerMode={false}
              />
            </Grid>
            <Grid item xs={12} md='auto'>
              <LoadingButton
                variant='outlined'
                onClick={handleClick}
                startIcon={<GetAppIcon />}
                isLoading={exportLoading}
                disabled={!!superAdminId || isPendingApproval}
                label='EXPORT DATA'
                fullWidth
              />
            </Grid>
            {isSessionRecordingEnabled && (
            <Grid item xs={12} md='auto'>
              <RecordSession
                isPendingApproval={isPendingApproval}
                fetchSessions={fetchSessionHandler}
                setToastProps={setToastProps}
              />
            </Grid>
            )}
            <Menu
              id='fade-menu'
              MenuListProps={{
                'aria-labelledby': 'fade-button',
              }}
              anchorEl={anchorEl as HTMLElement}
              open={open}
              onClose={handleMenuClose}
              TransitionComponent={Fade}
              sx={{ '& .MuiMenu-paper': { width: 152 } }}
            >
              <MenuItem onClick={() => exportData('excel')}>As Excel</MenuItem>
              <MenuItem onClick={() => exportData('pdf')}>As PDF</MenuItem>
            </Menu>
          </Grid>
        </Grid>
        {/* Bottom: Tags */}
        {chip && (
        <Box display='flex' gap={1} flexWrap='wrap'>
          <Box display='flex' alignItems='center' gap={1} mt={1}>
            <Tooltip
              placement='top'
              title={tooltip}
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: '#F2F2F2',
                    color: 'black',
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                    borderRadius: '4px',
                    padding: '8px',
                    height: 'auto',
                    maxWidth: '400px',
                  },
                },
                arrow: {
                  sx: {
                    color: '#F2F2F2',
                  },
                },
              }}
              arrow
            >
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>{chip}</span>
            </Tooltip>
          </Box>
        </Box>
        )}
      </HLCard>
    </Box>
  );
}

export default PatientDetailsPanel;
